// docs/116 CI12: the bar. The barkeep's chalked menu, and a row for each of my heroes so each orders
// for themselves; the mug slides down the counter to them when the order lands (the room's life plays
// the slide on the cheer it makes).
import { useMemo, useState } from 'react';
import { playSfx } from '../../../../../core/audio';
import { DRINKS, DRINK_NAME, DRINK_PRICE, type DrinkKind, type InnData } from '../types';
import { useInn } from '../store';
import { SidePanel, useOpenSound } from './common';

const MENU: Record<DrinkKind, { label: string; note: string }> = {
  ale: { label: 'Ale', note: 'brown, cool from the cellar' },
  cider: { label: 'Cider', note: 'sharp, from the orchard up the lane' },
  mead: { label: 'Mead', note: 'honey-sweet, in a horn' },
  wine: { label: 'Hot spiced wine', note: 'cloves and orange, steaming' },
};

const GREETINGS = [
  'What\'ll it be? Ale\'s fresh, the cider\'s fierce, and the wine\'s been warming since noon.',
  'Sit yourselves down. First sip is the best one, so make it a good one.',
  'Pick your poison, friends. Nothing here will poison you, mind. Probably.',
  'You look like folk who\'ve walked a long road. A mug each?',
];

export function BarPanel({ data, seed }: { data: InnData; seed: number }): React.JSX.Element {
  useOpenSound('inn.pour', 0.45);
  const mugs = useInn((s) => s.mugs);
  const [busy, setBusy] = useState<string>();
  const [said, setSaid] = useState<string>();
  const greeting = useMemo(() => GREETINGS[Math.abs(seed) % GREETINGS.length]!, [seed]);
  const order = async (p: InnData['payers'][number], d: DrinkKind) => {
    const key = `${p.key}-${d}`;
    setBusy(key);
    const ok = await data.act.drink(p.id, d);
    setBusy(undefined);
    if (ok) { playSfx('ui.coins', { gain: 0.4 }); setSaid(`${p.name} gets ${DRINK_NAME[d]}.`); }
    else { playSfx('ui.error'); setSaid(`${p.name} can't have that just now.`); }
  };
  return (
    <SidePanel name="bar" title="The bar" className="fk-innp__barp">
      <p className="fk-innp__keeper">“{greeting}”</p>
      <ul className="fk-innp__menu" aria-label="The menu">
        {DRINKS.map((d) => (
          <li key={d}><span className="fk-innp__menu-name">{MENU[d].label}</span><span className="fk-innp__menu-note">{MENU[d].note}</span><span className="fk-innp__menu-price">{DRINK_PRICE[d]}g</span></li>
        ))}
      </ul>
      {data.payers.length === 0 && <p className="fk-innp__faint">None of your heroes is here to order.</p>}
      <ul className="fk-innp__orders">
        {data.payers.map((p) => (
          <li key={p.key} className="fk-innp__order" data-testid="inn-order-row" data-hero={p.key}>
            <span className="fk-innp__order-who"><b>{p.name}</b><span className="fk-innp__gold">{p.gold}g</span>{mugs[p.key] && <span className="fk-innp__holding">holding {DRINK_NAME[mugs[p.key]!]}</span>}</span>
            <span className="fk-innp__order-drinks">
              {DRINKS.map((d) => (
                <button key={d} type="button" className="fk-innp__drink" disabled={!!busy || p.gold < DRINK_PRICE[d]} data-testid={`inn-drink-${p.key}-${d}`}
                  title={`${MENU[d].label} for ${p.name}, ${DRINK_PRICE[d]} gold`} onClick={() => void order(p, d)}>
                  {MENU[d].label.replace('Hot spiced wine', 'Wine')}<i>{DRINK_PRICE[d]}g</i>
                </button>
              ))}
            </span>
          </li>
        ))}
      </ul>
      {said && <p className="fk-innp__said" role="status">{said}</p>}
    </SidePanel>
  );
}
